import { useCallback, useEffect, useRef } from 'react';

import {
  FORBIDDEN_ZERO_BIT_MASK,
  NAL_TYPE,
  parseAnnexB,
  VCL_NAL_TYPE_MAX,
  VCL_NAL_TYPE_MIN,
} from '../utils/h264';

interface ExtendedVideoDecoder extends VideoDecoder {
  codec?: string;
}

interface UseVideoDecoderProps {
  onFrame: (frame: VideoFrame) => void;
  onDecoderError: (error: string) => void;
}

const arraysEqual = (a: Uint8Array, b: Uint8Array) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const useVideoDecoder = ({
  onFrame,
  onDecoderError,
}: UseVideoDecoderProps) => {
  const decoderRef = useRef<ExtendedVideoDecoder | null>(null);
  const hasReceivedKeyFrameRef = useRef(false);
  const currentSpsRef = useRef<Uint8Array | null>(null);
  const currentPpsRef = useRef<Uint8Array | null>(null);
  const isDecoderConfiguredWithDescriptionRef = useRef(false);

  // Keep dynamic references to callbacks to prevent reinstantiating the decoder when they change
  const onFrameRef = useRef(onFrame);
  const onDecoderErrorRef = useRef(onDecoderError);
  useEffect(() => {
    onFrameRef.current = onFrame;
  }, [onFrame]);
  useEffect(() => {
    onDecoderErrorRef.current = onDecoderError;
  }, [onDecoderError]);

  const handleDecoderError = useCallback((e: Error) => {
    console.error('VideoDecoder error:', e);
    // Reset keyframe state so we wait for a fresh keyframe after errors
    hasReceivedKeyFrameRef.current = false;
    onDecoderErrorRef.current(e.message);
  }, []);

  const createDecoderInstance = useCallback(() => {
    if (!('VideoDecoder' in window)) {
      const err = 'WebCodecs API is not supported in this browser.';
      onDecoderErrorRef.current(err);
      return null;
    }

    const newDecoder = new VideoDecoder({
      output: (frame) => onFrameRef.current(frame),
      error: handleDecoderError,
    }) as ExtendedVideoDecoder;

    return newDecoder;
  }, [handleDecoderError]);

  // Initial decoder configuration on mount
  useEffect(() => {
    let active = true;
    const initialCodec = 'avc1.42E01E';

    const setup = async () => {
      const newDecoder = createDecoderInstance();
      if (!newDecoder) return;

      try {
        const support = await VideoDecoder.isConfigSupported({
          codec: initialCodec,
          optimizeForLatency: true,
        });

        if (!active) {
          newDecoder.close();
          return;
        }

        if (support.supported) {
          newDecoder.configure({
            codec: initialCodec,
            optimizeForLatency: true,
          });
          newDecoder.codec = initialCodec;
          decoderRef.current = newDecoder;
        } else {
          const err = `Initial codec ${initialCodec} is not supported.`;
          console.error(err);
          onDecoderErrorRef.current(err);
          newDecoder.close();
        }
      } catch (e) {
        if (!active) return;
        const err = `Error configuring decoder: ${e instanceof Error ? e.message : String(e)}`;
        console.error(err);
        onDecoderErrorRef.current(err);
        newDecoder.close();
      }
    };

    setup();

    return () => {
      active = false;
      if (decoderRef.current) {
        decoderRef.current.close();
        decoderRef.current = null;
      }
    };
  }, [createDecoderInstance]);

  const reset = useCallback(() => {
    hasReceivedKeyFrameRef.current = false;
    currentSpsRef.current = null;
    currentPpsRef.current = null;
    isDecoderConfiguredWithDescriptionRef.current = false;
    // Flush any in-flight chunks from the previous stream and reconfigure
    // with the initial codec. The full description config will be applied
    // again when SPS/PPS arrive in the next stream.
    const decoder = decoderRef.current;
    if (decoder && decoder.state !== 'closed') {
      try {
        decoder.reset();
        decoder.configure({
          codec: 'avc1.42E01E',
          optimizeForLatency: true,
        });
        decoder.codec = 'avc1.42E01E';
      } catch (e) {
        console.warn('Failed to reset decoder:', e);
      }
    }
  }, []);

  const decode = useCallback(
    async (data: Uint8Array, timestamp: number) => {
      let decoder = decoderRef.current;
      if (!decoder) {
        console.warn('Decoder not initialized, dropping frame');
        return;
      }

      // Handle decoder in closed state - need to reinitialize
      if (decoder.state === 'closed') {
        decoder = createDecoderInstance();
        if (!decoder) return;

        // Reconfigure with last known codec or initial codec
        const codecToUse = decoderRef.current?.codec || 'avc1.42E01E';
        try {
          decoder.configure({
            codec: codecToUse,
            optimizeForLatency: true,
          });
          decoder.codec = codecToUse;
          hasReceivedKeyFrameRef.current = false;
          isDecoderConfiguredWithDescriptionRef.current = false;
          decoderRef.current = decoder;
        } catch (e) {
          console.error('Failed to reinitialize decoder:', e);
          return;
        }
      }

      if (decoder.state !== 'configured') {
        console.warn(
          `Decoder in unexpected state: ${decoder.state}, dropping frame`,
        );
        return;
      }

      try {
        // Parse NAL units from Annex B stream
        const nals = parseAnnexB(data);

        let hasRealIdrData = false; // True if we have type 5 NAL with actual payload
        let sawIdrMarker = false; // True if we saw any type 5 NAL (even 1-byte marker)
        let spsChanged = false;
        let ppsChanged = false;

        for (const nal of nals) {
          const forbiddenZeroBit = nal.data[0] & FORBIDDEN_ZERO_BIT_MASK;

          if (nal.type === NAL_TYPE.SPS && nal.data.length > 4) {
            // SPS - must have header + at least profile/constraints/level
            if (forbiddenZeroBit === 0) {
              if (
                !currentSpsRef.current ||
                !arraysEqual(currentSpsRef.current, nal.data)
              ) {
                currentSpsRef.current = new Uint8Array(nal.data);
                spsChanged = true;
              }
            } else {
              console.warn('Ignoring SPS with invalid forbidden_zero_bit');
            }
          } else if (nal.type === NAL_TYPE.PPS && nal.data.length > 1) {
            // PPS - must have more than just header byte
            if (forbiddenZeroBit === 0) {
              if (
                !currentPpsRef.current ||
                !arraysEqual(currentPpsRef.current, nal.data)
              ) {
                currentPpsRef.current = new Uint8Array(nal.data);
                ppsChanged = true;
              }
            } else {
              console.warn('Ignoring PPS with invalid forbidden_zero_bit');
            }
          } else if (nal.type === NAL_TYPE.IDR) {
            sawIdrMarker = true;
            if (nal.data.length > 1) {
              // IDR NAL with actual payload - this is a real keyframe
              hasRealIdrData = true;
            }
          }
        }

        // Reconfigure if SPS/PPS changed OR if the decoder was reinitialized and needs the full description config
        if (
          (spsChanged ||
            ppsChanged ||
            !isDecoderConfiguredWithDescriptionRef.current) &&
          currentSpsRef.current &&
          currentPpsRef.current
        ) {
          const profile = currentSpsRef.current[1];
          const constraints = currentSpsRef.current[2];
          const level = currentSpsRef.current[3];

          // Construct codec string with zeroed constraints for better browser compatibility.
          // The actual constraints are in the avcC description, so the codec string just
          // needs to identify profile/level. Using raw constraint flags (like 0xC0) can
          // cause Firefox to reject the codec even though Chrome accepts it.
          const codec = `avc1.${profile.toString(16).padStart(2, '0')}00${level.toString(16).padStart(2, '0')}`;

          // Keep the full codec string for logging/debugging
          const fullCodec = `avc1.${[profile, constraints, level]
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('')}`;

          // Create AVCDecoderConfigurationRecord (avcC)
          // Ref: ISO/IEC 14496-15
          const avcC = new Uint8Array(
            5 +
              1 +
              2 +
              currentSpsRef.current.length +
              1 +
              2 +
              currentPpsRef.current.length,
          );
          let offset = 0;
          avcC[offset++] = 1; // version
          avcC[offset++] = profile;
          avcC[offset++] = constraints;
          avcC[offset++] = level;
          avcC[offset++] = 0xff; // lengthSizeMinusOne = 3 (4 bytes) + reserved (111111)
          avcC[offset++] = 0xe1; // numOfSPS = 1 + reserved (111)
          avcC[offset++] = (currentSpsRef.current.length >> 8) & 0xff;
          avcC[offset++] = currentSpsRef.current.length & 0xff;
          avcC.set(currentSpsRef.current, offset);
          offset += currentSpsRef.current.length;
          avcC[offset++] = 1; // numOfPPS = 1
          avcC[offset++] = (currentPpsRef.current.length >> 8) & 0xff;
          avcC[offset++] = currentPpsRef.current.length & 0xff;
          avcC.set(currentPpsRef.current, offset);

          const config: VideoDecoderConfig = {
            codec: codec,
            description: avcC,
            optimizeForLatency: true,
          };

          // Check support before configuring
          // Try multiple codec strings since isConfigSupported can return true
          // even when decode will fail (known browser quirk)
          const codecsToTry = [
            codec, // Primary: zeroed constraints (most compatible)
            fullCodec, // Secondary: actual constraints from SPS
            `avc1.4200${level.toString(16).padStart(2, '0')}`, // Baseline with zero constraints
            `avc1.42e0${level.toString(16).padStart(2, '0')}`, // Constrained Baseline pattern
          ];

          let configured = false;
          for (const tryCodec of codecsToTry) {
            try {
              const tryConfig = { ...config, codec: tryCodec };
              const support = await VideoDecoder.isConfigSupported(tryConfig);
              if (support.supported) {
                decoder.configure(tryConfig);
                decoder.codec = tryCodec;
                configured = true;
                isDecoderConfiguredWithDescriptionRef.current = true;
                // After reconfiguring, we need a fresh IDR frame
                hasReceivedKeyFrameRef.current = false;
                break;
              }
            } catch (e) {
              console.warn(`Codec ${tryCodec} failed:`, e);
            }
          }

          if (!configured) {
            const err = 'No supported video codec found for this stream.';
            console.error(err);
            onDecoderErrorRef.current(err);
          }
        }

        // Start decoding when we see any IDR indicator (even 1-byte marker)
        if (!hasReceivedKeyFrameRef.current && sawIdrMarker) {
          hasReceivedKeyFrameRef.current = true;
        }

        if (hasReceivedKeyFrameRef.current) {
          // Construct AVCC format payload (Length-Prefixed)
          // Only include valid VCL NALs (types 1-5) with actual payload data.
          // Filter out:
          // - SPS (7) and PPS (8) - already in avcC description
          // - SEI (6), AUD (9), and other non-VCL NALs
          // - Unknown/extension NALs (types > 12)
          // - NALs with only header byte (no payload, length <= 1)
          const relevantNals = nals.filter((n) => {
            const isVclNal =
              n.type >= VCL_NAL_TYPE_MIN && n.type <= VCL_NAL_TYPE_MAX;
            const hasPayload = n.data.length > 1; // More than just NAL header
            return isVclNal && hasPayload;
          });

          const totalLength = relevantNals.reduce(
            (acc, nal) => acc + 4 + nal.data.length,
            0,
          );

          if (totalLength > 0) {
            const avccData = new Uint8Array(totalLength);
            let offset = 0;
            for (const nal of relevantNals) {
              const len = nal.data.length;
              avccData[offset++] = (len >> 24) & 0xff;
              avccData[offset++] = (len >> 16) & 0xff;
              avccData[offset++] = (len >> 8) & 0xff;
              avccData[offset++] = len & 0xff;
              avccData.set(nal.data, offset);
              offset += len;
            }

            // Determine chunk type based on whether we have ACTUAL IDR data
            // (not just an IDR marker with no payload)
            const chunkType = hasRealIdrData ? 'key' : 'delta';

            const chunk = new EncodedVideoChunk({
              type: chunkType,
              timestamp: timestamp,
              data: avccData,
            });
            decoder.decode(chunk);
          }
        }
      } catch (e) {
        console.error('Error decoding chunk:', e);
      }
    },
    [createDecoderInstance],
  );

  return {
    decode,
    reset,
  };
};
